import { useQuery, useMutation, useAction } from 'convex/react';
import { api } from '../../../../convex/_generated/api';
import { useEffect, useState, useRef } from 'react';

/**
 * Hook to get YNAB connection status
 */
export function useYnabConnection() {
  const connection = useQuery(api.ynab.getConnection);

  return {
    connection,
    isLoading: connection === undefined,
    isConnected: connection !== null && connection !== undefined,
  };
}

/**
 * Hook to auto-connect YNAB using env token if not connected
 * Call this in a top-level component after user is authenticated
 */
export function useAutoConnectYnab() {
  const sourceMode = useQuery(api.financeSources.mode);
  const { connection, isLoading, isConnected } = useYnabConnection();
  const connectWithToken = useAction(api.ynabActions.connectWithToken);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const attemptedRef = useRef(false);

  useEffect(() => {
    // Only attempt once, and only if not loading and not connected
    if (sourceMode !== 'ynab' || isLoading || isConnected || isConnecting || attemptedRef.current) {
      return;
    }

    // Connection query returned null - user is authenticated but no connection
    if (connection === null) {
      attemptedRef.current = true;
      setIsConnecting(true);

      // Try to connect with env token
      connectWithToken({})
        .then(() => {
          console.log('YNAB auto-connected successfully');
        })
        .catch((err) => {
          // This is expected if no env token is set
          console.log('YNAB auto-connect failed (may need manual setup):', err.message);
          setError(err.message);
        })
        .finally(() => {
          setIsConnecting(false);
        });
    }
  }, [connection, isLoading, isConnected, isConnecting, connectWithToken, sourceMode]);

  return { isConnecting, error };
}

/**
 * Hook to get YNAB accounts
 */
export function useYnabAccounts(includeClosed = false) {
  const accounts = useQuery(api.ynab.getAccounts, {
    includesClosed: includeClosed,
  });

  return {
    accounts: accounts ?? [],
    isLoading: accounts === undefined,
  };
}

/**
 * Hook to get YNAB categories
 */
export function useYnabCategories(includeHidden = false) {
  const categories = useQuery(api.ynab.getCategories, {
    includeHidden,
  });

  return {
    categories: categories ?? {},
    isLoading: categories === undefined,
  };
}

/**
 * Hook to get and manage transactions
 */
export function useTransactions(options?: {
  startDate?: string;
  endDate?: string;
  categoryId?: string;
  accountId?: string;
  needsReview?: boolean;
  search?: string;
  limit?: number;
}) {
  const result = useQuery(api.ynab.getTransactions, options ?? {});
  const updateTransaction = useMutation(api.ynab.updateTransaction);
  const markAsReviewed = useMutation(api.ynab.markAsReviewed);

  return {
    transactions: result?.transactions ?? [],
    hasMore: result?.hasMore ?? false,
    isLoading: result === undefined,
    updateTransaction,
    markAsReviewed,
  };
}

/**
 * Hook to get transactions needing review
 */
export function useTransactionsNeedingReview() {
  const transactions = useQuery(api.ynab.getTransactionsNeedingReview);
  const markAsReviewed = useMutation(api.ynab.markAsReviewed);

  return {
    transactions: transactions ?? [],
    isLoading: transactions === undefined,
    markAsReviewed,
  };
}

/**
 * Hook to manage YNAB connection
 */
export function useYnabActions() {
  const connectWithToken = useAction(api.ynabActions.connectWithToken);
  const getAuthUrl = useAction(api.ynabActions.getAuthUrl);
  const sync = useAction(api.ynabActions.sync);
  const disconnect = useMutation(api.ynab.disconnect);

  // Connect using Personal Access Token (recommended)
  const connectWithPersonalToken = async (accessToken?: string) => {
    return await connectWithToken({ accessToken });
  };

  // Connect using env token (uses YNAB_PERSONAL_ACCESS_TOKEN from Convex env)
  const connectWithEnvToken = async () => {
    return await connectWithToken({});
  };

  // Connect using OAuth (alternative - requires YNAB_CLIENT_ID)
  const connectWithOAuth = async () => {
    const redirectUri = `${window.location.origin}/findash/ynab/callback`;
    const authUrl = await getAuthUrl({ redirectUri });
    window.location.href = authUrl;
  };

  return {
    connectWithPersonalToken,
    connectWithEnvToken,
    connectWithOAuth,
    sync,
    disconnect,
  };
}
